import { useMemo } from 'react';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import { analyzePedigree } from './index';
import { structureHash } from './structureHash';

// Runs the pure analyzer over the live pedigree config, memoised on the
// structure hash (+ carrier inputs) so unrelated UI state changes don't
// recompute. Gated by Clinical Insights — returns null when disabled. `members`
// is the validatePedigreeConfig output; `carrierStatusByMember` is the byMember
// map from the shipped deriveCarrierStatus (reused, not recomputed).
export function usePedigreeAnalysis(
  members,
  {
    carrierStatusByMember = {},
    profileMatch = null,
    conditionCarrierEnabled = false
  } = {}
) {
  const flags = useVAFeatureFlags();
  // Fail-open while flags load (null) — matches the existing Clinical Insights gate.
  const enabled = flags ? flags.clinicalInsightsEnabled !== false : true;

  const hash = useMemo(
    () => (enabled ? structureHash(members || []) : ''),
    [members, enabled]
  );
  const carrierKey = useMemo(
    () => JSON.stringify(carrierStatusByMember || {}),
    [carrierStatusByMember]
  );

  return useMemo(() => {
    if (!enabled) return null;
    return analyzePedigree(members || [], {
      carrierStatusByMember,
      profileMatch,
      conditionCarrierEnabled
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, carrierKey, enabled, profileMatch, conditionCarrierEnabled]);
}
